import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import useIsVisible from "../hooks/useIsVisible";

const BATCH = 10;
const FIELD = "rounded border border-neutral-300 px-2 py-1";

export type TimelineEntry = { key: string | number; status: string; text: string; node: ReactNode };

/**
 * Elenco a card in stile "Cronologia del robot" (Lavori RPA) con scorrimento infinito
 * dentro un riquadro ad altezza fissa, filtro per stato e ricerca testuale.
 */
export default function NacTimeline({ entries, statuses }: { entries: TimelineEntry[]; statuses: string[] }) {
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState(BATCH);
  const box = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const atBottom = useIsVisible(sentinel);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => (!status || e.status === status) && (!q || e.text.toLowerCase().includes(q)));
  }, [entries, status, search]);
  const hasMore = shown < filtered.length;

  // Cambiando filtro o ricerca si riparte dall'inizio.
  useEffect(() => {
    setShown(BATCH);
    if (box.current) box.current.scrollTop = 0;
  }, [status, search]);

  // Il fondo della lista e' entrato nel riquadro: se ne caricano altre.
  useEffect(() => {
    if (atBottom && hasMore) setShown((n) => n + BATCH);
  }, [atBottom, hasMore, shown]);

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border border-neutral-200 bg-white p-3 text-sm">
        <div className="flex w-full items-center gap-2 sm:w-auto">
          {/* min-w-0: custom.css da' a ogni label 75px di larghezza minima. */}
          <label htmlFor="nac-history-status" className="min-w-0">
            Stato
          </label>
          <select id="nac-history-status" className={`${FIELD} min-w-0 flex-1 sm:flex-none`} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Tutti</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <label htmlFor="nac-history-search" className="min-w-0">
            Cerca
          </label>
          <input id="nac-history-search" className={`${FIELD} min-w-0 flex-1 sm:w-72 sm:flex-none`} placeholder="Nota o utente" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500">Nessuna voce trovata.</p>
      ) : (
        <div ref={box} className="max-h-[28rem] overflow-y-auto pr-1">
          <ol className="space-y-2">
            {filtered.slice(0, shown).map((e) => (
              <li key={e.key} className="rounded border border-neutral-200 bg-white p-3 text-sm">
                {e.node}
              </li>
            ))}
          </ol>
          <div ref={sentinel} className="py-2 text-center text-xs text-gray-500">
            {hasMore ? "Caricamento…" : ""}
          </div>
        </div>
      )}
    </>
  );
}
